import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Copy, Check, ExternalLink, Image, Facebook, AlertCircle, Edit2, MapPin } from 'lucide-react';
import { api } from '../../services/api';
import { ImageLightbox } from '../common/ImageLightbox';
import { ItemEditModal } from '../common/ItemEditModal';
import { type ItemWithImages, ITEM_CONDITION_LABELS, type ItemCondition } from '@findmystuff/shared';

interface FacebookMarketplaceSellModalProps {
  item: ItemWithImages;
  isOpen: boolean;
  onClose: () => void;
}

export function FacebookMarketplaceSellModal({ item, isOpen, onClose }: FacebookMarketplaceSellModalProps) {
  const queryClient = useQueryClient();

  // Fetch full item data with all images
  const { data: fullItem } = useQuery({
    queryKey: ['item', item.id],
    queryFn: () => api.items.get(item.id),
    enabled: isOpen,
  });

  // Use full item data if available, otherwise fall back to prop item
  const itemData = fullItem || item;

  // Copy state for each field
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Image lightbox state
  const [showImageLightbox, setShowImageLightbox] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);

  // Get all images from the full item data
  const allImages = itemData.images || [];
  const primaryImage = allImages[0];

  // Lightbox navigation
  const openLightbox = (index: number = 0) => {
    setLightboxImageIndex(index);
    setShowImageLightbox(true);
  };

  // Copy to clipboard helper
  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Get the high-res URL for an image
  const getImageUrl = (img: { originalUrl?: string | null; url: string }) => {
    return img.originalUrl || img.url;
  };

  // Get price display
  const getPrice = () => {
    if (itemData.discogsPrice) {
      return `$${itemData.discogsPrice.toFixed(2)}`;
    }
    if (itemData.attributes?.estimatedValue) {
      return `$${itemData.attributes.estimatedValue}`;
    }
    return null;
  };

  // Get condition label
  const getConditionLabel = () => {
    if (itemData.condition) {
      return ITEM_CONDITION_LABELS[itemData.condition as ItemCondition] || itemData.condition;
    }
    return null;
  };

  // Build optimized description for Facebook
  const buildFacebookDescription = () => {
    const parts: string[] = [];

    if (itemData.description) {
      parts.push(itemData.description);
    }

    const condition = getConditionLabel();
    if (condition) {
      parts.push(`\nCondition: ${condition}`);
    }

    if (itemData.attributes?.brand) {
      parts.push(`Brand: ${itemData.attributes.brand}`);
    }

    if (itemData.attributes?.model) {
      parts.push(`Model: ${itemData.attributes.model}`);
    }

    if (itemData.category) {
      parts.push(`Category: ${itemData.category}`);
    }

    return parts.join('\n');
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-[#1877F2] rounded-t-xl">
            <div className="flex items-center gap-2 text-white">
              <Facebook className="w-5 h-5" />
              <h2 className="text-lg font-semibold">Facebook Marketplace</h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center gap-1.5 text-sm text-white/90 hover:text-white font-medium px-2 py-1"
              >
                <Edit2 className="w-4 h-4" />
                Edit Item
              </button>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Info banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Manual Posting Required</p>
                <p className="text-blue-600 mt-1">
                  Facebook Marketplace doesn't have a public API. Copy the details below and paste them when creating your listing on Facebook.
                </p>
              </div>
            </div>

            {/* Item Preview */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-start gap-3">
                {primaryImage ? (
                  <button
                    onClick={() => openLightbox(0)}
                    className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-400 transition-colors"
                  >
                    <img
                      src={primaryImage.thumbnailUrl || primaryImage.url}
                      alt={itemData.title}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ) : (
                  <div className="w-20 h-20 flex-shrink-0 bg-gray-200 rounded-lg flex items-center justify-center">
                    <Image className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{itemData.title}</h3>
                  {getConditionLabel() && (
                    <p className="text-sm text-gray-600">{getConditionLabel()}</p>
                  )}
                  {getPrice() && (
                    <p className="text-lg font-bold text-emerald-600 mt-1">{getPrice()}</p>
                  )}
                </div>
              </div>

              {/* Image thumbnails */}
              {allImages.length > 1 && (
                <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                  {allImages.slice(0, 6).map((img, index) => (
                    <button
                      key={img.id}
                      onClick={() => openLightbox(index)}
                      className="w-12 h-12 flex-shrink-0 rounded overflow-hidden border border-gray-200 hover:border-blue-400 transition-colors"
                    >
                      <img
                        src={img.thumbnailUrl || img.url}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                  {allImages.length > 6 && (
                    <div className="w-12 h-12 flex-shrink-0 rounded bg-gray-200 flex items-center justify-center text-sm text-gray-500 font-medium">
                      +{allImages.length - 6}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Copy Fields */}
            <div className="space-y-3">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={itemData.title}
                    readOnly
                    className="input flex-1 bg-gray-50"
                  />
                  <button
                    onClick={() => copyToClipboard(itemData.title, 'title')}
                    className="btn-secondary px-3 flex items-center gap-1"
                  >
                    {copiedField === 'title' ? (
                      <>
                        <Check className="w-4 h-4 text-green-600" />
                        <span className="text-green-600">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Price */}
              {getPrice() && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={getPrice() || ''}
                      readOnly
                      className="input flex-1 bg-gray-50"
                    />
                    <button
                      onClick={() => copyToClipboard(getPrice()!.replace('$', ''), 'price')}
                      className="btn-secondary px-3 flex items-center gap-1"
                    >
                      {copiedField === 'price' ? (
                        <>
                          <Check className="w-4 h-4 text-green-600" />
                          <span className="text-green-600">Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <div className="relative">
                  <textarea
                    value={buildFacebookDescription()}
                    readOnly
                    rows={5}
                    className="input w-full bg-gray-50 pr-20 resize-none"
                  />
                  <button
                    onClick={() => copyToClipboard(buildFacebookDescription(), 'description')}
                    className="absolute top-2 right-2 btn-secondary px-2 py-1 text-sm flex items-center gap-1"
                  >
                    {copiedField === 'description' ? (
                      <>
                        <Check className="w-3 h-3 text-green-600" />
                        <span className="text-green-600">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Images - Individual Links */}
              {allImages.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Images ({allImages.length}) — click to open, then right-click to save
                  </label>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {allImages.map((img, index) => (
                      <a
                        key={img.id}
                        href={getImageUrl(img)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative group aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-400 transition-colors"
                      >
                        <img
                          src={img.thumbnailUrl || img.url}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                          <ExternalLink className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <span className="absolute bottom-1 right-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                          {index + 1}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Location */}
              {(itemData.location || itemData.locationImage) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Location
                  </label>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    {itemData.locationImage && (
                      <img
                        src={itemData.locationImage}
                        alt="Location"
                        className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                      />
                    )}
                    {itemData.location && (
                      <span className="text-sm text-gray-700">{itemData.location}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t bg-gray-50 rounded-b-xl">
            <a
              href="https://www.facebook.com/marketplace/create/item"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary w-full flex items-center justify-center gap-2 bg-[#1877F2] hover:bg-[#166FE5]"
            >
              <ExternalLink className="w-4 h-4" />
              Open Facebook Marketplace
            </a>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Opens in a new tab. Paste the copied details into your listing.
            </p>
          </div>
        </div>
      </div>

      {/* Image Lightbox */}
      {showImageLightbox && allImages.length > 0 && (
        <ImageLightbox
          images={allImages}
          initialIndex={lightboxImageIndex}
          onClose={() => setShowImageLightbox(false)}
          title={itemData.title}
          itemId={item.id}
        />
      )}

      {/* Edit Item Modal */}
      <ItemEditModal
        itemId={item.id}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          // Refresh item data when edit modal closes
          queryClient.invalidateQueries({ queryKey: ['item', item.id] });
        }}
      />
    </>
  );
}
